import { useCallback, useRef } from "react";

const backupSuffix = () => new Date().toISOString().replace(/[-:.TZ]/g, "");

export const pendingTaskStorageKey = (baseKey: string, userId?: number | null): string | null => {
	const id = Number(userId);
	if (!Number.isFinite(id) || id < 1) return null;
	return `${baseKey}:${id}`;
};

const postedWorkSessionSuppressionKey = (baseKey: string, userId?: number | null): string | null => {
	const scoped = pendingTaskStorageKey(baseKey, userId);
	return scoped ? `${scoped}:posted-work-sessions` : null;
};

export const loadPostedWorkSessionSuppressions = (
	baseKey: string,
	userId?: number | null,
): Set<string> => {
	const key = postedWorkSessionSuppressionKey(baseKey, userId);
	if (!key) return new Set();
	try {
		const parsed: unknown = JSON.parse(localStorage.getItem(key) || "[]");
		return Array.isArray(parsed) ? new Set(parsed.map(String)) : new Set();
	} catch {
		localStorage.removeItem(key);
		return new Set();
	}
};

export const persistPostedWorkSessionSuppressions = (
	baseKey: string,
	userId: number | null | undefined,
	ids: Iterable<string>,
): void => {
	const key = postedWorkSessionSuppressionKey(baseKey, userId);
	if (!key) return;
	const unique = [...new Set(ids)];
	if (unique.length) localStorage.setItem(key, JSON.stringify(unique));
	else localStorage.removeItem(key);
};

export const pendingTaskKeyLabelBase = (key: string): string => key.replace(/:\d+$/, "");

export const persistPendingTasks = (
	baseKey: string,
	userId: number | null | undefined,
	raw: string,
): void => {
	const scoped = pendingTaskStorageKey(baseKey, userId);
	if (!scoped) return;
	localStorage.setItem(scoped, raw);
};

/** Revive a draft's line items; anything but an array means no lines. */
export const revivePendingItems = <T,>(
	raw: unknown,
	construct: (row: any) => T,
): T[] => (Array.isArray(raw) ? raw.map((row) => construct(row)) : []);

/** Copy unreadable drafts aside. Returns false when the original key must be kept. */
export const backupFailedPendingTasks = (storageKey: string, raw: string): boolean => {
	const backupKey = `${storageKey}-${backupSuffix()}`;
	try {
		localStorage.setItem(backupKey, raw);
	} catch (error) {
		console.error(`Failed to backup ${storageKey} to ${backupKey}:`, error);
		return false;
	}
	localStorage.removeItem(storageKey);
	return true;
};

/**
 * Load pending tasks from localStorage. On parse/revive failure, copies the raw
 * payload to a timestamped backup key before clearing the live key.
 */
export const loadPendingTasks = <T,>(
	storageKey: string,
	revive: (task: any) => T,
	userId?: number | null,
): T[] | null => {
	const scoped = pendingTaskStorageKey(storageKey, userId);
	if (!scoped) return null;
	const stored = localStorage.getItem(scoped);
	if (!stored) return null;

	try {
		const parsed = JSON.parse(stored);
		if (!Array.isArray(parsed) || parsed.length === 0) return null;
		const suppressedIds = loadPostedWorkSessionSuppressions(storageKey, userId);
		const visible = suppressedIds.size
			? parsed.filter((task) => !suppressedIds.has(String(task?.id)))
			: parsed;
		if (visible.length !== parsed.length) localStorage.setItem(scoped, JSON.stringify(visible));
		return visible.map(revive);
	} catch (error) {
		console.error(`Failed to parse ${scoped} from localStorage:`, error);
		backupFailedPendingTasks(scoped, stored);
		return null;
	}
};

/** Pending-task counts keyed by `source` (POS / depot id) for the signed-in user. */
export const pendingTaskCountsBySource = (
	storageKey: string,
	userId?: number | null,
): Map<number, number> => {
	const tasks = loadPendingTasks(storageKey, (task) => task, userId) || [];
	const counts = new Map<number, number>();
	for (const task of tasks as { source?: number | string }[]) {
		const source = Number(task?.source);
		if (!Number.isFinite(source)) continue;
		counts.set(source, (counts.get(source) || 0) + 1);
	}
	return counts;
};

/** Skip the first effect run so persist cannot overwrite drafts before hydrate. */
export const useSkipFirstRun = () => {
	const skip = useRef(true);
	return useCallback(() => {
		if (!skip.current) return false;
		skip.current = false;
		return true;
	}, []);
};
